// ThemeContext.js
import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'

import { Theme } from '@/config/app'
import { isZendeskApp } from '@/features/zendesk-app'

// Create a context with default values
const ThemeContext = createContext({
  theme: Theme.Dark,
  toggleTheme: () => {},
})

// Custom hook to access the theme context
export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

export const ThemeProvider = ({ children }: PropsWithChildren<object>) => {
  const [theme, setTheme] = useState<Theme>(() => {
    // Check if a theme is stored in local storage
    const defaultTheme = isZendeskApp() ? Theme.Light : Theme.Dark
    const storedTheme = localStorage.getItem('theme')

    return storedTheme ? parseInt(storedTheme) : defaultTheme
  })

  // Update local storage when the theme changes
  useEffect(() => {
    localStorage.setItem('theme', theme.toString())
  }, [theme])

  const toggleTheme = () => {
    setTheme((prevTheme: Theme) => (prevTheme === Theme.Light ? Theme.Dark : Theme.Light))
  }

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>
}
